import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {inject, Signal} from '@angular/core';
import {Store} from '@ngrx/store';
import {toSignal} from '@angular/core/rxjs-interop';
import {selectSeApiToken} from '../../../common/context-data/context-data.selectors';
import {CommerceDomainService} from './commerce-domain.service';

export enum HttpMethod {
  DELETE = 'DELETE',
  GET = 'GET',
  PUT = 'PUT',
  PATCH = 'PATCH',
  POST = 'POST',
}

export interface ApiRequestOptions {
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  responseType?: never;
  withCredentials?: boolean;
}

export interface ApiService {
  GET<T>(url: string, options?: ApiRequestOptions): Observable<T>;

  PATCH<T>(
    url: string,
    data: unknown,
    options?: ApiRequestOptions,
  ): Observable<T>;

  POST<T>(
    url: string,
    data: unknown,
    options?: ApiRequestOptions,
  ): Observable<T>;

  POST<T>(
    url: string,
    data: unknown,
    options?: ApiRequestOptions,
  ): Observable<T>;

  PUT<T>(
    url: string,
    data: unknown,
    options?: ApiRequestOptions,
  ): Observable<T>;

  DELETE<T>(url: string, options?: ApiRequestOptions): Observable<T>;
}

export abstract class CommerceApiService implements ApiService {
  seApiToken: Signal<string | undefined>;
  readonly commerceDomainService: CommerceDomainService;

  abstract readonly controllerPath: string;

  protected constructor(
    protected http: HttpClient,
    protected store: Store,
  ) {
    this.commerceDomainService = inject(CommerceDomainService);

    this.seApiToken = toSignal(this.store.select(selectSeApiToken));
  }

  DELETE<T>(url: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.delete<T>(url, options);
  }

  GET<T>(url: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.get<T>(url, options);
  }

  PATCH<T>(
    url: string,
    data: unknown,
    options?: ApiRequestOptions,
  ): Observable<T> {
    return this.http.patch<T>(url, data, options);
  }

  PUT<T>(
    url: string,
    data: unknown,
    options?: ApiRequestOptions,
  ): Observable<T> {
    return this.http.put<T>(url, data, options);
  }

  POST<TReturn>(
    url: string,
    data: unknown,
    options?: ApiRequestOptions,
  ): Observable<TReturn> {
    return this.http.post<TReturn>(url, data, options);
  }

  getUrl(path = ''): string {
    return this.commerceDomainService.getUrl(path);
  }

  protected getOptions(src?: Partial<ApiRequestOptions>): ApiRequestOptions {
    const options: ApiRequestOptions = {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      ...src,
    };

    if (this.seApiToken()) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${this.seApiToken()}`,
      };
    }

    return options;
  }
}
